import { ReactNode } from 'react';

import { SPECIAL_METHOD } from '@/constants';
import { CartLineItem, ISpecial } from '@/types';
import {
  BogoAddUx,
  BogoClassifyParams,
  getBogoAddUxForAddLine,
  getBogoAddUxForIncrement,
} from '../../hooks/useBundleSpecial/bogoClassify';
import { isBogoGetProduct } from '@/utils/bogoSpecialCart';
import { TNotifyType } from '@/hooks/useNotify';

type NotifyFn = (_type: TNotifyType, _message: ReactNode) => void;

type NullableText = string | null | undefined;

/** Shared toast bodies — single source for the UNLOCKED / Added / COMPLETED copy. */
const unlockedToast = (
  dealTitle: NullableText,
  productName: NullableText,
): ReactNode => (
  <p>
    DEAL {dealTitle} UNLOCKED! <br />
    {`Added ${productName}`}
  </p>
);

const addedToast = (
  dealTitle: NullableText,
  productName: NullableText,
): ReactNode => <p>{`Added ${productName} to Deal ${dealTitle}`}</p>;

const completedToast = (
  dealTitle: NullableText,
  productName: NullableText,
): ReactNode => (
  <p>
    DEAL {dealTitle} COMPLETED! <br />
    {`Added ${productName}`}
  </p>
);

export const notifyBogoUxToasts = (
  notify: NotifyFn,
  ux: BogoAddUx,
  dealTitle: NullableText,
  productName: NullableText,
): void => {
  if (ux.toast === 'unlocked') {
    notify('success', unlockedToast(dealTitle, productName));
  }

  if (ux.toast === 'added') {
    notify('success', addedToast(dealTitle, productName));
  }

  if (ux.toast === 'completed') {
    notify('success', completedToast(dealTitle, productName));
  }
};

export const runBogoDealPageAddNotify = ({
  notify,
  special,
  item,
  isGetAdd,
  classifyParams,
  productName,
}: {
  notify: NotifyFn;
  special: ISpecial;
  item: CartLineItem;
  isGetAdd: boolean;
  classifyParams: BogoClassifyParams;
  productName: NullableText;
}): boolean => {
  const ux = getBogoAddUxForAddLine(
    classifyParams,
    item,
    isGetAdd,
    special.independentProductsRequired,
  );

  notifyBogoUxToasts(notify, ux, special.title, productName);

  return ux.openCart;
};

export const runBogoIncrementNotify = ({
  notify,
  special,
  classifyParams,
  incrementLineId,
  isGetIncrement,
  productName,
}: {
  notify: NotifyFn;
  special: ISpecial;
  classifyParams: BogoClassifyParams;
  incrementLineId: string;
  isGetIncrement: boolean;
  productName: NullableText;
}): boolean => {
  const ux = getBogoAddUxForIncrement(
    classifyParams,
    incrementLineId,
    isGetIncrement,
    special.independentProductsRequired,
  );

  notifyBogoUxToasts(notify, ux, special.title, productName);

  return ux.openCart;
};

export const notifyCatalogBogoAdd = ({
  notify,
  special,
  itemId,
  itemSalePrice,
  independentProductsCount,
  productName,
}: {
  notify: NotifyFn;
  special: ISpecial;
  itemId: NullableText;
  itemSalePrice: number;
  independentProductsCount: number;
  productName: NullableText;
}): void => {
  if (special.bundleComplete) {
    return;
  }

  const isBuyAdd =
    !!itemId && !isBogoGetProduct(special, itemId, itemSalePrice);
  const completesBuyRequirement =
    independentProductsCount + 1 === special.independentProductsRequired;

  if (isBuyAdd && completesBuyRequirement) {
    notify('success', unlockedToast(special.title, productName));
    return;
  }

  notify('success', addedToast(special.title, productName));
};

export const notifyNonBogoBundleAdd = ({
  notify,
  special,
  productName,
  dependentProductsCount,
  independentProductsCount,
}: {
  notify: NotifyFn;
  special: ISpecial;
  productName: NullableText;
  dependentProductsCount: number;
  independentProductsCount: number;
}): void => {
  const bundleUnitsInCart = dependentProductsCount + independentProductsCount;

  if (bundleUnitsInCart >= special.totalProductsRequired) {
    return;
  }

  if (independentProductsCount + 1 === special.independentProductsRequired) {
    notify('success', unlockedToast(special.title, productName));
    return;
  }

  notify('success', addedToast(special.title, productName));
};

export const notifyRegularProductAdd = ({
  notify,
  productName,
}: {
  notify: NotifyFn;
  productName: NullableText;
}): void => {
  notify('success', <p>{`Added ${productName}`}</p>);
};

export const resolveShouldOpenCartOnAdd = ({
  bundleId,
  special,
  bogoOpenCart,
}: {
  bundleId?: string;
  special?: ISpecial;
  bogoOpenCart: boolean;
}): boolean => {
  const isDealAdd = Boolean(bundleId);

  if (!isDealAdd) {
    return false;
  }

  if (special?.method === SPECIAL_METHOD.BOGO) {
    return bogoOpenCart;
  }

  return !!special?.bundleComplete;
};
